import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Activity, HardDrive, Phone, Clock, CheckCircle, Users,
  FileText, AlertTriangle, RefreshCw, Globe, Building2,
  Lock, Network, Shield, Ban,
} from "lucide-react";
import { toast } from "sonner";

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDuration(secs) {
  if (!secs) return "0s";
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m${s > 0 ? `${s}s` : ""}`;
}

function fmtTs(ts, tz) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (isNaN(d)) return ts.slice(11, 19) || ts;
    return d.toLocaleTimeString("fr-FR", {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      ...(tz ? { timeZone: tz } : {}),
    });
  } catch { return "—"; }
}

function fmtAgo(ts) {
  if (!ts) return "—";
  try {
    const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    return `${Math.floor(diff / 3600)}h`;
  } catch { return "—"; }
}

const _MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };

// Le serveur Asterisk journalise en UTC : "[Jun 22 20:21:13] …". On convertit le
// préfixe horaire vers le fuseau de l'hôtel pour l'affichage. Année non présente
// dans le log → année courante (suffisant pour un tail récent).
function logLineToTz(line, tz, year) {
  const m = line.match(/^\[([A-Z][a-z]{2}) +(\d+) +(\d{2}):(\d{2}):(\d{2})\]/);
  if (!m || !tz) return line;
  const mon = _MONTHS[m[1]];
  if (mon === undefined) return line;
  const d = new Date(Date.UTC(year, mon, +m[2], +m[3], +m[4], +m[5]));
  if (isNaN(d)) return line;
  const local = d.toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    timeZone: tz,
  }).replace(",", "");
  return line.replace(m[0], `[${local}]`);
}

function logLineColor(line) {
  if (/\bERROR\b/.test(line)) return "text-red-400";
  if (/\bWARNING\b|\bWARN\b/.test(line)) return "text-amber-400";
  if (/\bNOTICE\b/.test(line)) return "text-blue-400";
  if (/\bVERBOSE\b|\bDEBUG\b/.test(line)) return "text-zinc-500";
  return "text-zinc-300";
}

function statColor(val, good, warn) {
  if (val >= good) return "emerald";
  if (val >= warn) return "amber";
  return "red";
}

// ── Sub-components ────────────────────────────────────────────────────────────

function HourlyBarChart({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  const barW = 100 / 24;
  const chartH = 72;
  return (
    <svg viewBox="0 0 100 84" preserveAspectRatio="none" className="w-full h-28" style={{ display: "block" }}>
      {/* Subtle grid lines */}
      {[0.25, 0.5, 0.75].map((f) => (
        <line key={f} x1={0} y1={chartH * (1 - f)} x2={100} y2={chartH * (1 - f)} stroke="#27272a" strokeWidth="0.25" strokeDasharray="1,1" />
      ))}
      {/* Hour markers */}
      {[6, 12, 18].map((h) => (
        <g key={h}>
          <line x1={h * barW} y1={0} x2={h * barW} y2={chartH + 2} stroke="#3f3f46" strokeWidth="0.3" />
          <text x={h * barW + 0.3} y={chartH + 8} fontSize="2.8" fill="#71717a" fontFamily="monospace">{h}h</text>
        </g>
      ))}
      {data.map((d) => {
        const barH = Math.max((d.count / max) * (chartH - 2), d.count > 0 ? 1.5 : 0);
        const x = d.hour * barW + barW * 0.12;
        const w = barW * 0.76;
        const y = chartH - barH;
        const isOffPeak = d.hour < 7 || d.hour > 22;
        const isPeak = d.count > max * 0.7;
        const fill = isOffPeak ? "#3f3f46" : isPeak ? "#f59e0b" : "#2563eb";
        return (
          <g key={d.hour}>
            <rect x={x} y={y} width={w} height={barH} rx="0.5" fill={fill} opacity={d.count > 0 ? 0.9 : 0.15} />
            <title>{`${d.hour}h00 — ${d.count} appels`}</title>
          </g>
        );
      })}
    </svg>
  );
}

const KPI_ACCENT = { zinc: "border-t-zinc-200", emerald: "border-t-emerald-500", amber: "border-t-amber-400", red: "border-t-red-500", blue: "border-t-blue-500" };
const KPI_VALUE  = { zinc: "text-zinc-900", emerald: "text-emerald-600", amber: "text-amber-500", red: "text-red-500", blue: "text-blue-600" };

function Kpi({ icon: Icon, label, value, sub, color = "zinc" }) {
  return (
    <div className={`rounded-sm border border-zinc-200 bg-white p-4 border-t-2 ${KPI_ACCENT[color]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-400">{label}</span>
        <Icon className="h-3.5 w-3.5 text-zinc-300" />
      </div>
      <div className={`mt-2 font-head text-2xl font-black tracking-tighter ${KPI_VALUE[color]}`}>{value}</div>
      {sub && <div className="mt-0.5 font-mono text-[10px] text-zinc-400">{sub}</div>}
    </div>
  );
}

function LogTerminal({ lines, loading, tz, year }) {
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [lines]);
  const shown = tz ? lines.map((l) => logLineToTz(l, tz, year)) : lines;
  return (
    <div className="overflow-y-auto rounded-sm border border-zinc-800 font-mono text-[11px] leading-5" style={{ background: "#0d1117", maxHeight: "340px" }}>
      <div className="flex items-center gap-1.5 border-b border-zinc-800 px-4 py-2">
        <div className="h-2 w-2 rounded-full bg-red-500 opacity-80" />
        <div className="h-2 w-2 rounded-full bg-amber-500 opacity-80" />
        <div className="h-2 w-2 rounded-full bg-emerald-500 opacity-80" />
        <span className="ml-2 text-zinc-600 text-[10px]">/var/log/asterisk/full</span>
        {loading && <span className="ml-auto text-zinc-600 text-[10px] animate-pulse">chargement…</span>}
      </div>
      <div className="p-3 space-y-0.5">
        {lines.length === 0 && !loading && <span className="text-zinc-600">Aucun log disponible — agent non joignable.</span>}
        {shown.map((line, i) => (
          <div key={i} className={`${logLineColor(line)} break-all whitespace-pre-wrap`}>{line}</div>
        ))}
        <div ref={bottomRef} />
        {lines.length > 0 && (
          <div className="flex items-center gap-0.5 text-emerald-500">
            <span>›</span>
            <span className="ml-0.5 inline-block h-3 w-1.5 bg-emerald-500 animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
}

// Tableau de requêtes provisioning (HTTPS / HTTP / TFTP partagent la même structure)
function ProvTable({ logs, loading, emptyMsg, showHotel = false, tz }) {
  return (
    <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
      <div className="flex items-center border-b border-zinc-200 px-5 py-3">
        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Requêtes récentes</h2>
        {loading && <span className="ml-3 animate-pulse text-[10px] text-zinc-400">chargement…</span>}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100 text-left text-[9px] uppercase tracking-[0.15em] text-zinc-400">
            <th className="px-5 py-2 font-bold">Heure</th>
            <th className="px-5 py-2 font-bold">Fichier / MAC</th>
            <th className="px-5 py-2 font-bold">Extension</th>
            {showHotel && <th className="px-5 py-2 font-bold">Hôtel</th>}
            <th className="px-5 py-2 font-bold">IP client</th>
            <th className="px-5 py-2 font-bold">Statut</th>
          </tr>
        </thead>
        <tbody>
          {logs.slice(0, 50).map((log, i) => (
            <tr key={i} className={`border-b border-zinc-50 text-xs last:border-0 transition-colors hover:bg-zinc-50/60 ${!log.found ? "bg-amber-50/30" : ""}`}>
              <td className="px-5 py-2.5 font-mono text-[11px] text-zinc-400 tabular-nums">{fmtTs(log.ts, tz)}</td>
              <td className="px-5 py-2.5 font-mono text-[11px] text-zinc-600 max-w-[160px] truncate">{log.filename}</td>
              <td className="px-5 py-2.5">
                {log.extension ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="rounded-sm bg-zinc-900 px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-wider text-zinc-100">
                      {log.extension}
                    </span>
                    {log.room_number && <span className="font-mono text-[10px] text-zinc-400">ch.{log.room_number}</span>}
                  </span>
                ) : <span className="font-mono text-[11px] text-zinc-300">—</span>}
              </td>
              {showHotel && (
                <td className="px-5 py-2.5">
                  {log.hotel_name
                    ? <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-600">
                        <Building2 className="h-3 w-3 text-zinc-300 shrink-0" />
                        <span className="truncate max-w-[120px]">{log.hotel_name}</span>
                      </span>
                    : <span className="font-mono text-[11px] text-zinc-300">—</span>}
                </td>
              )}
              <td className="px-5 py-2.5 font-mono text-[11px] text-zinc-400">{log.client_ip}</td>
              <td className="px-5 py-2.5">
                {log.found
                  ? <span className="inline-flex items-center rounded-sm bg-emerald-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-emerald-700">200</span>
                  : <span className="inline-flex items-center rounded-sm bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-amber-700">404</span>}
              </td>
            </tr>
          ))}
          {logs.length === 0 && !loading && (
            <tr><td colSpan={showHotel ? 6 : 5} className="px-5 py-8 text-center font-mono text-xs text-zinc-400">{emptyMsg}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ProvKpis({ logs, loading, tz }) {
  const total = logs.length;
  const found = logs.filter((l) => l.found).length;
  const notFound = total - found;
  const lastTs = logs[0]?.ts || null;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Kpi icon={FileText} label="Requêtes totales" value={total} sub="en mémoire" color="blue" />
      <Kpi icon={CheckCircle} label="Fichiers servis" value={found} sub="200 OK"
        color={total > 0 ? statColor((found / total) * 100, 90, 60) : "zinc"} />
      <Kpi icon={AlertTriangle} label="Introuvables" value={notFound} sub="404 — MAC inconnu"
        color={notFound > 0 ? "amber" : "zinc"} />
      <Kpi icon={Clock} label="Dernière requête" value={lastTs ? fmtAgo(lastTs) : "—"}
        sub={lastTs ? fmtTs(lastTs, tz) : "aucune"} />
    </div>
  );
}

function UnknownDevices({ logs }) {
  const unknownFiles = [...new Set(logs.filter((l) => !l.found).map((l) => l.filename))];
  if (!unknownFiles.length) return null;
  return (
    <div className="rounded-sm border border-amber-200 bg-amber-50/50 p-4">
      <h3 className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">
        <AlertTriangle className="h-3.5 w-3.5" />Appareils inconnus ({unknownFiles.length})
      </h3>
      <div className="flex flex-wrap gap-2">
        {unknownFiles.map((f) => (
          <span key={f} className="rounded-sm border border-amber-200 bg-white px-2 py-1 font-mono text-xs text-amber-800">{f}</span>
        ))}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

const TABS = [
  { id: "asterisk", label: "Asterisk",      icon: Activity, perServer: true  },
  { id: "https",    label: "HTTPS Agent",   icon: Lock,     perServer: true  },
  { id: "http",     label: "HTTP Backend",  icon: Globe,    perServer: false },
  { id: "tftp",     label: "TFTP",          icon: Network,  perServer: true  },
  { id: "fail2ban", label: "Sécurité",      icon: Shield,   perServer: true  },
];

export default function Logs() {
  const [servers, setServers]           = useState([]);
  const [hotels, setHotels]             = useState([]);
  const [serverId, setServerId]         = useState(null);
  const [hotelFilter, setHotelFilter]   = useState("");
  const [tab, setTab]                   = useState("asterisk");
  const [refreshKey, setRefreshKey]     = useState(0);

  const [astStats, setAstStats]         = useState(null);
  const [astLogs, setAstLogs]           = useState([]);
  const [astLoading, setAstLoading]     = useState(false);

  const [httpsLogs, setHttpsLogs]       = useState([]);
  const [httpsLoading, setHttpsLoading] = useState(false);

  const [httpLogs, setHttpLogs]         = useState([]);
  const [httpLoading, setHttpLoading]   = useState(false);

  const [tftpLogs, setTftpLogs]         = useState([]);
  const [tftpLoading, setTftpLoading]   = useState(false);

  const [f2b, setF2b]                   = useState(null);
  const [f2bLoading, setF2bLoading]     = useState(false);
  const [banIp, setBanIp]               = useState("");

  useEffect(() => {
    api.get("/servers").then((r) => {
      setServers(r.data);
      if (r.data.length > 0) setServerId(r.data[0].id);
    }).catch(() => {});
    api.get("/hotels").then((r) => setHotels(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "http")  { loadHttp();  return; }
    if (!serverId)       return;
    if (tab === "asterisk") loadAsterisk();
    if (tab === "https")    loadHttps();
    if (tab === "tftp")     loadTftp();
    if (tab === "fail2ban") loadFail2ban();
  }, [serverId, tab, refreshKey]);

  async function loadFail2ban() {
    setF2bLoading(true);
    try {
      const r = await api.get(`/servers/${serverId}/fail2ban`).catch(() => ({ data: null }));
      setF2b(r.data);
    } finally { setF2bLoading(false); }
  }

  const unbanIp = async (ip) => {
    try {
      const { data } = await api.post(`/servers/${serverId}/fail2ban/unban`, { ip });
      if (data?.ok) { toast.success(`${ip} débanni`); loadFail2ban(); }
      else toast.error(data?.error || data?.output || "Échec du débannissement");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doBanIp = async () => {
    const ip = banIp.trim();
    if (!ip) return;
    try {
      const { data } = await api.post(`/servers/${serverId}/fail2ban/ban`, { ip });
      if (data?.ok) { toast.success(`${ip} banni`); setBanIp(""); loadFail2ban(); }
      else toast.error(data?.error || data?.output || "Échec du bannissement");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  async function loadAsterisk() {
    setAstLoading(true);
    try {
      const [sRes, lRes] = await Promise.all([
        api.get(`/servers/${serverId}/asterisk-stats`).catch(() => ({ data: null })),
        api.get(`/servers/${serverId}/asterisk-logs`).catch(() => ({ data: { lines: [] } })),
      ]);
      setAstStats(sRes.data);
      setAstLogs(lRes.data?.lines || []);
    } finally { setAstLoading(false); }
  }

  async function loadHttps() {
    setHttpsLoading(true);
    try {
      const r = await api.get(`/servers/${serverId}/provision-logs`).catch(() => ({ data: { logs: [] } }));
      setHttpsLogs(r.data?.logs || []);
    } finally { setHttpsLoading(false); }
  }

  async function loadHttp() {
    setHttpLoading(true);
    try {
      const r = await api.get("/provisioning/logs/http").catch(() => ({ data: { logs: [] } }));
      setHttpLogs(r.data?.logs || []);
    } finally { setHttpLoading(false); }
  }

  async function loadTftp() {
    setTftpLoading(true);
    try {
      const r = await api.get(`/servers/${serverId}/tftp-logs`).catch(() => ({ data: { events: [] } }));
      setTftpLogs(r.data?.events || []);
    } finally { setTftpLoading(false); }
  }

  // Apply hotel filter to a log array
  const byHotel = (logs) => {
    if (!hotelFilter) return logs;
    return logs.filter((l) => l.hotel_name === hotelFilter);
  };

  const currentTabDef = TABS.find((t) => t.id === tab);
  const showServerSelect = currentTabDef?.perServer;

  // Fuseau horaire d'affichage : celui de l'hôtel sélectionné, sinon le 1er hôtel,
  // sinon Martinique (parc actuel). Les horodatages des logs sont en UTC côté API.
  const selectedHotel = hotels.find((h) => h.name === hotelFilter);
  const tz = selectedHotel?.timezone || hotels[0]?.timezone || "America/Martinique";

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Infrastructure</p>
          <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">Journaux & Diagnostics</h1>
        </div>
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="mt-2 flex items-center gap-1.5 rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-700"
        >
          <RefreshCw className="h-3 w-3" />Actualiser
        </button>
      </div>

      {/* Selectors row */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {showServerSelect && (
          <div className="flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-zinc-400" />
            <select
              value={serverId || ""}
              onChange={(e) => setServerId(e.target.value)}
              className="rounded-sm border border-zinc-200 bg-white px-3 py-1.5 font-mono text-sm text-zinc-700 focus:border-blue-500 focus:outline-none"
            >
              {servers.map((s) => (
                <option key={s.id} value={s.id}>{s.name} — {s.ip}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-zinc-400" />
          <select
            value={hotelFilter}
            onChange={(e) => setHotelFilter(e.target.value)}
            className="rounded-sm border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700 focus:border-blue-500 focus:outline-none"
          >
            <option value="">Tous les hôtels</option>
            {hotels.map((h) => (
              <option key={h.id} value={h.name}>{h.name}</option>
            ))}
          </select>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-sm bg-zinc-100 px-2 py-1 font-mono text-[10px] text-zinc-500">
          <Clock className="h-3 w-3 text-zinc-400" />
          {tz}
        </span>
      </div>

      {/* Tabs */}
      <div className="mt-5 flex border-b border-zinc-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
              tab === id ? "border-blue-600 text-blue-600" : "border-transparent text-zinc-400 hover:text-zinc-600"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>

      {/* ── Asterisk ────────────────────────────────────────────────────────── */}
      {tab === "asterisk" && (
        <div className="mt-5 space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi icon={Phone} label="Total appels" value={astStats?.total_calls ?? "—"} sub="toutes extensions" color="blue" />
            <Kpi icon={Clock} label="Durée moy." value={astStats ? fmtDuration(astStats.avg_duration) : "—"} sub="appels aboutis" />
            <Kpi icon={CheckCircle} label="Taux succès" value={astStats ? `${astStats.success_rate}%` : "—"} sub="ANSWERED / total"
              color={astStats ? statColor(astStats.success_rate, 90, 70) : "zinc"} />
            <Kpi icon={Users} label="Extensions" value={astStats?.unique_exts ?? "—"} sub="sources uniques" />
          </div>
          {astStats?.by_hour && (
            <div className="rounded-sm border border-zinc-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Volume d'appels — 24h</span>
                <div className="flex items-center gap-3 text-[9px] text-zinc-400">
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-blue-600" />normal</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-amber-400" />pic</span>
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-zinc-600" />off-peak</span>
                </div>
              </div>
              <HourlyBarChart data={astStats.by_hour} />
              <div className="mt-1 flex justify-between font-mono text-[9px] text-zinc-400">
                <span>00h</span><span>06h</span><span>12h</span><span>18h</span><span>23h</span>
              </div>
            </div>
          )}
          <div>
            <h2 className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Log Asterisk — tail</h2>
            <LogTerminal lines={astLogs} loading={astLoading} tz={tz} year={new Date().getFullYear()} />
          </div>
        </div>
      )}

      {/* ── HTTPS Agent ─────────────────────────────────────────────────────── */}
      {tab === "https" && (
        <div className="mt-5 space-y-6">
          <ProvKpis logs={byHotel(httpsLogs)} loading={httpsLoading} tz={tz} />
          <ProvTable
            logs={byHotel(httpsLogs)}
            loading={httpsLoading}
            emptyMsg="Aucune requête — l'agent surveille depuis son démarrage."
            showHotel={!hotelFilter}
            tz={tz}
          />
          <UnknownDevices logs={byHotel(httpsLogs)} />
        </div>
      )}

      {/* ── HTTP Backend ─────────────────────────────────────────────────────── */}
      {tab === "http" && (
        <div className="mt-5 space-y-6">
          <ProvKpis logs={byHotel(httpLogs)} loading={httpLoading} tz={tz} />
          <ProvTable
            logs={byHotel(httpLogs)}
            loading={httpLoading}
            emptyMsg="Aucune requête — les téléphones n'ont pas encore contacté le backend HTTP."
            showHotel={!hotelFilter}
            tz={tz}
          />
          <UnknownDevices logs={byHotel(httpLogs)} />
        </div>
      )}

      {/* ── TFTP ────────────────────────────────────────────────────────────── */}
      {tab === "tftp" && (
        <div className="mt-5 space-y-6">
          <ProvKpis logs={byHotel(tftpLogs)} loading={tftpLoading} tz={tz} />
          <ProvTable
            logs={byHotel(tftpLogs)}
            loading={tftpLoading}
            emptyMsg="Aucun transfert TFTP — tftpd-hpa non actif ou aucun téléphone Cisco sur ce serveur."
            showHotel={!hotelFilter}
            tz={tz}
          />
          <UnknownDevices logs={byHotel(tftpLogs)} />
        </div>
      )}

      {/* ── Sécurité / fail2ban ─────────────────────────────────────────────── */}
      {tab === "fail2ban" && (
        <div className="mt-5 space-y-6">
          {f2b && !f2b.ok && (
            <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              fail2ban indisponible : {f2b.error || "agent non joignable"}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi icon={Ban} label="Bannis actifs" value={f2b?.currently_banned ?? "—"} sub={`jail ${f2b?.jail || "asterisk"}`}
              color={f2b?.currently_banned > 0 ? "red" : "emerald"} />
            <Kpi icon={Shield} label="Total bannis" value={f2b?.total_banned ?? "—"} sub="depuis démarrage" color="blue" />
            <Kpi icon={AlertTriangle} label="Échecs suivis" value={f2b?.currently_failed ?? "—"} sub="fenêtre courante" color="amber" />
            <Kpi icon={FileText} label="Total échecs" value={f2b?.total_failed ?? "—"} sub="auth SIP" color="zinc" />
          </div>

          {/* Bannir manuellement */}
          <div className="flex flex-wrap items-center gap-2 rounded-sm border border-zinc-200 bg-white px-4 py-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Bannir une IP</span>
            <input
              data-testid="f2b-ban-input"
              value={banIp}
              onChange={(e) => setBanIp(e.target.value)}
              placeholder="ex : 89.190.159.181"
              className="w-52 rounded-sm border border-zinc-200 px-2 py-1.5 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <button
              data-testid="f2b-ban-btn"
              onClick={doBanIp}
              disabled={!banIp.trim()}
              className="rounded-sm bg-zinc-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-zinc-700 disabled:opacity-30 transition-colors"
            >
              Bannir
            </button>
          </div>

          {/* Liste des bannis */}
          <div className="overflow-hidden rounded-sm border border-zinc-200 bg-white">
            <div className="flex items-center border-b border-zinc-200 px-5 py-3">
              <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">IP bannies</h2>
              {f2bLoading && <span className="ml-3 animate-pulse text-[10px] text-zinc-400">chargement…</span>}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(f2b?.banned || []).map((ip) => (
                  <tr key={ip} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/60">
                    <td className="px-5 py-2.5 font-mono text-xs text-zinc-700">{ip}</td>
                    <td className="px-5 py-2.5 text-right">
                      <button
                        data-testid={`f2b-unban-${ip}`}
                        onClick={() => unbanIp(ip)}
                        className="rounded-sm border border-zinc-200 bg-white px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:border-emerald-300 hover:text-emerald-600 transition-colors"
                      >
                        Débannir
                      </button>
                    </td>
                  </tr>
                ))}
                {f2b?.ok && (f2b.banned || []).length === 0 && (
                  <tr><td className="px-5 py-8 text-center text-sm text-zinc-400">Aucune IP bannie — RAS.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Whitelist */}
          {f2b?.ignoreip?.length > 0 && (
            <div className="rounded-sm border border-zinc-200 bg-white px-5 py-4">
              <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Réseaux protégés (ignoreip)</h2>
              <p className="mt-1 text-xs text-zinc-500">Jamais bannis — LAN voix de l'hôtel, VPN et loopback.</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {f2b.ignoreip.map((net) => (
                  <span key={net} className="rounded-sm bg-emerald-50 px-2 py-0.5 font-mono text-[11px] text-emerald-700">{net}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
